<template>
  <form-section-checkbox
    :title="$t('ProjectSettings_EngagementConditions_Frequency_Capping_Title')"
    :description="
      $t('ProjectSettings_EngagementConditions_Frequency_Capping_Description')
    "
    v-model="frequencyCappingEnabled"
    :disabled="readOnly"
  >
    <el-row>
      <el-col class="column-container">
        <div class="label-container">
          <div class="field-container">
            <label class="form-label">
              {{ $t('Engagement_Category_Apply_Across_Channels') }}</label
            >
            <form-item-i-tooltip placement="top-start" raw-content>
              <template #content>
                <span
                  v-html="$t('Engagement_Category_Apply_Across_Channels_Tip')"
                ></span>
                <a
                  href="https://docs.xtremepush.com/docs/engagement-rules-categories#frequency-capping"
                  target="_blank"
                >
                  {{
                    $t(
                      'Engagement_Category_Apply_Across_Channels_Tip_Link_Text'
                    )
                  }}</a
                >
              </template>
            </form-item-i-tooltip>
          </div>
          <p class="description">
            {{ $t('Engagement_Category_Apply_Across_Channels_Description') }}
          </p>
        </div>
        <div class="field-container">
          <rest-form-item style="display: flex;" prop="apply_across_channels">
            <el-switch
              v-model="model.apply_across_channels"
              prop="apply_across_channels"
              :disabled="readOnly"
              data-qa="apply-across-channels-switch"
            />
          </rest-form-item>
        </div>
      </el-col>
    </el-row>

    <el-row>
      <el-col class="column-container">
        <div class="label-container">
          <div class="field-container">
            <label class="form-label">
              {{ $t('Engagement_Category_Maximum_Sends') }}
            </label>
          </div>
          <p class="description">
            {{ $t('Engagement_Category_Maximum_Sends_Description') }}
          </p>
        </div>
        <div class="field-container">
          <rest-form-item prop="max_per_day" style="display: flex;">
            <div style="display: flex">
              <el-input
                placeholder="∞"
                v-model="model.max_per_day"
                :disabled="readOnly"
                prop="max_per_day"
                @change="validateFields"
                style="width: 90px"
                data-qa="max-sends-per-day-input"
              />
              <label class="label-item">{{
                $t('Engagement_Category_Max_Day')
              }}</label>
            </div>
          </rest-form-item>
          <rest-form-item prop="max_per_week" style="display: flex;">
            <div style="display: flex">
              <el-input
                placeholder="∞"
                v-model="model.max_per_week"
                :disabled="readOnly"
                prop="max_per_week"
                @change="validateFields"
                style="width: 90px"
                data-qa="max-sends-per-week-input"
              />
              <label class="label-item">{{
                $t('Engagement_Category_Max_Week')
              }}</label>
            </div>
          </rest-form-item>
          <rest-form-item prop="max_per_month" style="display: flex;">
            <div style="display: flex">
              <el-input
                placeholder="∞"
                v-model="model.max_per_month"
                :disabled="readOnly"
                prop="max_per_month"
                @change="validateFields"
                style="width: 90px"
                data-qa="max-sends-per-month-input"
              />
              <label class="label-item">{{
                $t('Engagement_Category_Max_Month')
              }}</label>
            </div>
          </rest-form-item>
        </div>
      </el-col>
    </el-row>

    <el-row v-if="$hasFeature('lifetimeSends')">
      <el-col class="column-container">
        <div class="label-container">
          <div class="field-container">
            <label class="form-label">
              {{ $t('Engagement_Category_Lifetime_Sending_Limit') }}
            </label>
            <form-item-i-tooltip placement="top-start">
              <template #content>
                <span>{{
                  $t('Engagement_Category_Lifetime_Sending_Limit_Tip')
                }}</span>
                <a
                  href="https://docs.xtremepush.com/docs/engagement-rules-categories#frequency-capping"
                  target="_blank"
                  >{{
                    $t('Engagement_Category_Lifetime_Sending_Limit_Link_Tip')
                  }}</a
                >
              </template>
            </form-item-i-tooltip>
          </div>
          <p class="description">
            {{ $t('Engagement_Category_Lifetime_Sending_Limit_Description') }}
          </p>
        </div>
        <div class="field-container">
          <rest-form-item prop="max_lifetime_sends">
            <el-input
              placeholder="∞"
              v-model="model.max_lifetime_sends"
              :disabled="readOnly"
              style="width: 90px"
              data-qa="max-lifetime-sends-input"
            />
          </rest-form-item>
        </div>
      </el-col>
    </el-row>

    <el-row>
      <el-col class="cooldown-period-ct column-container">
        <div class="label-container">
          <label class="form-label">{{
            $t('Engagement_Category_Cooldown_Period')
          }}</label>
          <p class="description">
            {{ $t('Engagement_Category_Cooldown_Description') }}
          </p>
        </div>
        <div style="display: flex; align-items: center; column-gap: 20px;">
          <rest-form-item prop="cooldown_amount" style="display: flex;">
            <el-input
              placeholder="∞"
              v-model="model.cooldown_amount"
              :disabled="readOnly"
              @change="validateCooldown"
              style="width: 90px"
              data-qa="cooldown-period-input"
            />
          </rest-form-item>
          <rest-form-item prop="cooldown_measure" style="display: flex">
            <enum-select
              v-model="model.cooldown_measure"
              :options="categoryCooldownLabels"
              :disabled="readOnly"
              style="width: 150px"
              data-qa="cooldown-period-dropdown"
            />
          </rest-form-item>
        </div>
      </el-col>
    </el-row>
  </form-section-checkbox>
</template>

<script>
import FormSectionCheckbox from '@/common/components/form/FormSectionCheckbox'
import RestFormItem from '@/common/components/rest/RestFormItem'
import EnumSelect from '@/common/components/form/EnumSelect'
import { categoryCooldownLabels } from '@/common/enum/CategoryEnums'
import FormItemITooltip from '@/common/components/form/FormItemITooltip.vue'

export default {
  name: 'EngagementCategoriesFrequencyCappingSection',
  components: {
    FormItemITooltip,
    EnumSelect,
    RestFormItem,
    FormSectionCheckbox
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    limitsEnabled: {
      type: Boolean,
      required: true
    },
    readOnly: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      categoryCooldownLabels
    }
  },
  methods: {
    validateFields() {
      this.$emit('validateFields')
    },
    validateCooldown() {
      this.$emit('validateCooldown')
    }
  },
  computed: {
    frequencyCappingEnabled: {
      get: function() {
        return Number(this.limitsEnabled)
      },
      set: function(value) {
        this.$emit('frequencyCappingChange', !!value)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
>>> .content .content {
  padding-left: 32px;
  padding-top: 32px;
}
.column-container {
  display: flex;
  column-gap: 41px;
  margin-bottom: 32px;
}
.label-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 220px;
}
.field-container {
  display: flex;
  align-items: center;
  margin-top: 6px;
}
.form-item-ct {
  display: flex;
  flex-direction: row;
}
.description {
  color: #7e8596;
  font-size: 12px;
  line-height: 18px;
}
.label-item {
  margin-left: 10px;
  margin-right: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.form-label {
  color: #5c6374;
  font-size: 14px;
}
.cooldown-period-ct {
  margin-top: 6px;
}

[role='tooltip'] a {
  color: #1d87c8;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaigns/components/EngagementCategoriesFrequencyCappingSection.vue